/**
 * Emission — emit a (progressive) compte-rendu from a validated demande (POST /results/demandes/:id/
 * emettre). Gated on results.compterendu.create (route-level + the submit button). NOVA-057: the report
 * TYPE (partiel / complet) is DERIVED server-side from the validated-result coverage (VAL-6) — there is no
 * client scope to pick. The user confirms the governed action (emission freezes a version + opens the
 * diffusion channels), and on success is routed to the new CR detail. Server may answer
 * { emitted:false, reason } when no validated result exists yet — surfaced as a non-error notice.
 * 5 states: offline (banner + disabled submit), the rest are inline (no list fetch on this screen).
 */
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button, Card } from "@/ui";
import { useToast } from "@/ui";
import { useT, useUiStore } from "@/app";
import { useCan } from "@/auth";
import { ForbiddenState } from "@/ui";
import { PageHeader } from "./components/PageHeader";
import { ConfirmModal } from "./components/ConfirmModal";
import { RESULTS_CAPS } from "./caps";
import { useEmitCompteRendu } from "./hooks";

export function EmissionPage() {
  const { t } = useT();
  const navigate = useNavigate();
  const params = useParams<{ id: string }>();
  const demandeId = params.id ?? null;
  const online = useUiStore((s) => s.online);
  const toast = useToast();
  const canEmit = useCan(RESULTS_CAPS.create);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const emit = useEmitCompteRendu(demandeId);

  if (!canEmit) {
    return <ForbiddenState requiredCapabilities={[RESULTS_CAPS.create]} />;
  }

  const submit = () => {
    emit.mutate(
      undefined,
      {
        onSuccess: (result) => {
          setConfirmOpen(false);
          if (result.emitted) {
            toast.success(
              t("results.emission.success", { numero: result.numero, version: result.version }),
            );
            navigate(`/resultats/comptes-rendus/${result.compte_rendu_id}`);
          } else {
            const reasonKey = `results.emission.reason.${result.reason}`;
            const reason = t(reasonKey);
            toast.warn(reason === reasonKey ? t("results.emission.noResult") : reason);
          }
        },
        onError: (err) => {
          setConfirmOpen(false);
          toast.error(err.message || t("results.emission.error"));
        },
      },
    );
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-5)" }}>
      <PageHeader
        title={t("results.emission.title")}
        subtitle={
          <span>
            {t("results.emission.demande")}{" "}
            <span className="nv-mono nv-ltr-island">{demandeId ?? "—"}</span>
          </span>
        }
        actions={
          <Button variant="ghost" leadingIcon="‹" onClick={() => navigate("/resultats")}>
            {t("results.emission.back")}
          </Button>
        }
      />

      {!online && (
        <p
          role="status"
          style={{
            margin: 0,
            padding: "var(--nv-s-2) var(--nv-s-3)",
            borderRadius: "var(--nv-r-sm)",
            background: "var(--nv-tint-high)",
            color: "var(--nv-warn)",
            fontSize: "var(--nv-fs-caption)",
          }}
        >
          ⚠ {t("state.offline.title")}
        </p>
      )}

      <Card title={t("results.emission.legend")}>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (online && demandeId) setConfirmOpen(true);
          }}
          style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-4)", maxWidth: 480 }}
        >
          {/* NOVA-057 — the report type is derived server-side (VAL-6); no operator scope picker. */}
          <p style={{ margin: 0, color: "var(--nv-slate)", fontSize: "var(--nv-fs-caption)" }}>
            {t("results.emission.derivedNote")}
          </p>

          <Button
            type="submit"
            leadingIcon="✚"
            disabled={!online || !demandeId}
            loading={emit.isPending}
          >
            {t("results.emission.submit")}
          </Button>
        </form>
      </Card>

      <ConfirmModal
        open={confirmOpen}
        title={t("results.emission.confirmTitle")}
        body={t("results.emission.confirmBody")}
        confirmLabel={t("results.emission.confirm")}
        busy={emit.isPending}
        onConfirm={submit}
        onClose={() => !emit.isPending && setConfirmOpen(false)}
      />
    </div>
  );
}
